// components/Timeline/Timeline.tsx
import React, { useEffect, useRef, useState } from 'react';
import { TimelineProps } from "features/breaches";
import './Timeline.css';
// Import des icônes de react-icons
import {
  MdEmail,
  MdLock,
  MdPhone,
  MdPerson,
  MdHome,
  MdCake,
  MdPublic,
  MdCreditCard,
  MdAccountBalance,
  MdShare,
  MdBadge,
  MdWork,
  MdAttachMoney,
  MdVpnKey,
  MdHelp,
  MdDevices,
  MdShoppingCart,
  MdSchool,
  MdComputer,
  MdLocationOn,
  MdFingerprint,
  MdSecurity
} from 'react-icons/md';
import { FaUserSecret, FaUserFriends } from 'react-icons/fa';
import { GiWallet } from 'react-icons/gi';
import { IconType } from 'react-icons';

// Dictionnaire de traduction pour les DataClasses
const commonTranslations: Record<string, string> = {
  'Email addresses': 'Adresses email',
  'Passwords': 'Mots de passe',
  'Phone numbers': 'Numéros de téléphone',
  'Names': 'Noms',
  'Bank account numbers': 'numéros de compte bancaire',
  'Spoken languages': 'Langues parlées',
  'Physical addresses': 'Adresses postale',
  'Physical address': 'Adresse postale',
  'Dates of birth': 'Dates de naissance',
  'IP addresses': 'Adresses IP',
  'Geographic locations': 'Localisations géographiques',
  'Credit cards': 'Cartes de crédit',
  'Bank accounts': 'Comptes bancaires',
  'Social media profiles': 'Profils sociaux',
  'Usernames': "Noms d'utilisateur",
  'Genders': 'Genres',
  'Job titles': 'Titres professionnels',
  'Salary information': 'Informations salariales',
  'Government IDs': 'Identifiants gouvernementaux',
  'Private keys': 'Clés privées',
  'Security questions': 'Questions de sécurité',
  'Device information': "Informations sur l'appareil",
  'Browser user agent': 'Agent utilisateur',
  'Purchases': 'Achats',
  'Education': 'Éducation',
  'Email': 'Email',
  'Password': 'Mot de passe',
  'Phone': 'Téléphone',
  'Name': 'Nom',
  'Address': 'Adresse',
  'Birth date': 'Date de naissance',
  'IP': 'Adresse IP',
  'Location': 'Localisation',
  'Credit card': 'Carte de crédit',
  'Bank account': 'Compte bancaire',
  'Social media': 'Réseaux sociaux',
  'Username': "Nom d'utilisateur",
  'Gender': 'Genre',
  'Job title': 'Titre professionnel',
  'Salary': 'Salaire',
  'Government ID': "Identifiant gouvernemental",
  'Private key': 'Clé privée',
  'Security question': 'Question de sécurité',
  'Device': 'Appareil',
  'Browser': 'Navigateur',
  'Purchase': 'Achat',
  'Cryptocurrency wallet addresses': 'Adresses de portefeuilles de cryptomonnaies',
};

// Dictionnaire d'icônes - utilisation du type IconType de react-icons
const dataClassIcons: Record<string, IconType> = {
  // Emails
  'Email addresses': MdEmail,
  'Email': MdEmail,

  // Mots de passe
  'Passwords': MdLock,
  'Password': MdLock,

  // Téléphone
  'Phone numbers': MdPhone,
  'Phone': MdPhone,

  // Noms et identité
  'Names': MdPerson,
  'Name': MdPerson,
  'Usernames': FaUserFriends,
  'Username': FaUserFriends,
  'Genders': MdPerson,
  'Gender': MdPerson,

  // Adresses
  'Physical addresses': MdHome,
  'Address': MdHome,
  'Geographic locations': MdLocationOn,
  'Location': MdLocationOn,

  // Dates
  'Dates of birth': MdCake,
  'Birth date': MdCake,

  // IP et appareils
  'IP addresses': MdComputer,
  'IP': MdComputer,
  'Device information': MdDevices,
  'Device': MdDevices,
  'Browser user agent': MdPublic,
  'Browser': MdPublic,

  // Financier
  'Credit cards': MdCreditCard,
  'Credit card': MdCreditCard,
  'Bank accounts': MdAccountBalance,
  'Bank account': MdAccountBalance,
  'Salary information': MdAttachMoney,
  'Salary': MdAttachMoney,
  'Purchases': MdShoppingCart,
  'Purchase': MdShoppingCart,

  // Social
  'Social media profiles': MdShare,
  'Social media': MdShare,

  // Professionnel
  'Job titles': MdWork,
  'Job title': MdWork,
  'Education': MdSchool,

  // Sécurité
  'Government IDs': MdBadge,
  'Government ID': MdBadge,
  'Private keys': MdVpnKey,
  'Private key': MdVpnKey,
  'Security questions': MdHelp,
  'Security question': MdHelp,

  // Crypto
  'Cryptocurrency wallet addresses': GiWallet,

  // Default
  'default': MdFingerprint
};

// Fonction pour obtenir l'icône appropriée
const getDataClassIcon = (dataClass: string): IconType => {
  // Chercher une correspondance exacte
  if (dataClassIcons[dataClass]) {
    return dataClassIcons[dataClass];
  }

  // Chercher une correspondance partielle
  for (const [key, Icon] of Object.entries(dataClassIcons)) {
    if (key !== 'default' && dataClass.toLowerCase().includes(key.toLowerCase())) {
      return Icon;
    }
  }

  // Retourner l'icône par défaut
  return dataClassIcons['default'];
};

// Fonction pour traduire un dataClass
const translateDataClass = (dataClass: string): string => {
  // Vérifier si c'est déjà en français
  const frenchPattern = /[éèêëàâîïôûùçœ]/i;
  if (frenchPattern.test(dataClass)) {
    return dataClass;
  }

  // Chercher dans le dictionnaire
  return commonTranslations[dataClass] || dataClass;
};

const Timeline: React.FC<TimelineProps> = ({ breaches, showDescription = true }) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState(0);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Extraire l'année de BreachDate et trier du plus récent au plus ancien
  const sortedBreaches = [...breaches].sort((a, b) => {
    const dateA = new Date(a.breach.BreachDate);
    const dateB = new Date(b.breach.BreachDate);
    return dateB.getTime() - dateA.getTime();
  });

  // Formater la date en français
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).replace(/\./g, '');
  };

  // Calculer la hauteur de la ligne
  useEffect(() => {
    if (!timelineRef.current || cardsRef.current.length === 0) return;

    const calculateLineHeight = () => {
      const timelineRect = timelineRef.current?.getBoundingClientRect();
      if (!timelineRect) return;

      const firstCard = cardsRef.current[0];
      const lastCard = cardsRef.current[cardsRef.current.length - 1];

      if (firstCard && lastCard) {
        const firstRect = firstCard.getBoundingClientRect();
        const lastRect = lastCard.getBoundingClientRect();

        const startY = firstRect.top - timelineRect.top + 20;
        const endY = lastRect.bottom - timelineRect.top - 20;

        setLineHeight(endY - startY);
      }
    };

    calculateLineHeight();
    window.addEventListener('resize', calculateLineHeight);

    return () => window.removeEventListener('resize', calculateLineHeight);
  }, [sortedBreaches]);

  // Animation au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('timeline-event-visible');
          }
        });
      },
      { threshold: 0.2, rootMargin: '50px' }
    );

    document.querySelectorAll('.timeline-event').forEach((event) => observer.observe(event));
    document.querySelectorAll('.timeline-year-marker').forEach((year) => observer.observe(year));

    return () => observer.disconnect();
  }, [sortedBreaches]);

  if (!breaches || breaches.length === 0) {
    return (
      <div className="timeline-container timeline-empty" ref={timelineRef}>
        <div className="timeline-message">
          <svg className="timeline-empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#666" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#666" strokeWidth="2"/>
            <path d="M12 16H12.01" stroke="#666" strokeWidth="2"/>
          </svg>
          <h3>Aucune fuite détectée</h3>
          <p>Super, il semblerait que vos données n'aient pas fuité.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-container" ref={timelineRef}>
      {/* Simple timeline line on the left */}
      <div className="timeline-line" style={{ height: lineHeight ? `${lineHeight}px` : '100%' }}></div>

      <div className="timeline-events">
        {sortedBreaches.map((breach, index) => {
          const breachYear = new Date(breach.breach.BreachDate).getFullYear();
          const prevBreachYear = index > 0
            ? new Date(sortedBreaches[index - 1].breach.BreachDate).getFullYear()
            : null;

          const showYear = index === 0 || breachYear !== prevBreachYear;

          return (
            <React.Fragment key={breach.breachId}>
              {showYear && (
                <div className="timeline-year-marker timeline-event-hidden">
                  <div className="timeline-year-card">
                    <svg className="timeline-year-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                      <path d="M8 2V6" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16 2V6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{breachYear}</span>
                  </div>
                </div>
              )}

              <div
                ref={el => cardsRef.current[index] = el}
                className="timeline-event timeline-event-hidden"
                data-index={index}
              >
                <div className="timeline-card">
                  <div className="d-flex flex-row justify-content-between align-items-center py-3 flex-wrap">
                    <div className="timeline-card-header">
                      {breach.breach.LogoPath && (
                        <div className="timeline-logo-container">
                          <img
                            src={breach.breach.LogoPath}
                            alt={`Logo ${breach.breach.Title}`}
                            className="timeline-logo"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                            }}
                          />
                        </div>
                      )}
                      <div className="timeline-card-title">
                        <h3>{breach.breach.Title}</h3>
                      </div>
                    </div>
                    <div>
                      <span className="timeline-date">
                        <time dateTime={breach.breach.BreachDate}>
                          {formatDate(breach.breach.BreachDate)}
                        </time>
                      </span>
                    </div>
                  </div>

                  <div className={`timeline-card-content ${!showDescription ? "description-hidden" : ""}`}>
                    {showDescription && (
                      <p className="timeline-description-text">
                        {breach.breach.Description}
                      </p>
                    )}

                    <div className="timeline-data-classes">
                      <h4>Données concernées :</h4>
                      <div className={`data-classes-tags ${breach.breach.DataClasses.length > 2 ? "two-columns" : ""}`}>
                        {breach.breach.DataClasses.map((dataClass, idx) => {
                          const translatedDataClass = translateDataClass(dataClass);
                          return (
                            <span key={idx} className="data-class-tag">
                              <span className="data-class-text">{translatedDataClass}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    <div className="timeline-status">
                      <span className="status-badge status-source">
                        <ul>
                          {breach?.email && (
                            <li>Email : {breach.email}</li>
                          )}
                          <li>Source : Have I Been Pwned</li>
                        </ul>
                      </span>
                      {breach.breach.PwnCount > 0 && (
                        <span className="pwn-count">
                          {breach.breach.PwnCount.toLocaleString()} comptes impactés
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;